import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { useSDK } from "../lib/sdk-context";
import { api } from "../lib/api";
import { Button } from "../components/ui/Button";
import { useToast } from "../components/ui/Toast";

interface AuditRow {
  id: string;
  brand_id: string;
  source_row_id: string | null;
  license_number: string | null;
  action: string;
  prev_status: string | null;
  new_status: string | null;
  actor_heady_user_id: string | null;
  actor_heady_team_id: string | null;
  ip_address: string | null;
  user_agent: string | null;
  payload: Record<string, unknown> | null;
  occurred_at: string;
}

interface ActorRow {
  actor_heady_user_id: string;
  latest_ua: string | null;
  event_count: number;
  last_seen: string;
}

const ACTION_OPTIONS: { value: string; label: string }[] = [
  { value: "confirm_match", label: "Confirm match" },
  { value: "pick_different_retailer", label: "Change retailer" },
  { value: "unconfirm", label: "Unconfirm" },
  { value: "mark_delivery_partner", label: "Delivery partner" },
  { value: "mark_not_a_retailer", label: "Not a retailer" },
  { value: "skip", label: "Skip" },
  { value: "enrichment_applied", label: "Enrichment" },
];

const ACTION_COLORS: Record<string, string> = {
  confirm_match: "bg-green-100 text-green-800 border-green-200",
  pick_different_retailer: "bg-amber-100 text-amber-800 border-amber-200",
  unconfirm: "bg-amber-100 text-amber-800 border-amber-200",
  enrichment_applied: "bg-blue-100 text-blue-800 border-blue-200",
  mark_delivery_partner: "bg-purple-100 text-purple-800 border-purple-200",
  mark_not_a_retailer: "bg-red-100 text-red-800 border-red-200",
  skip: "bg-gray-100 text-gray-700 border-gray-200",
  brand_supplied_website: "bg-blue-100 text-blue-800 border-blue-200",
  brand_supplied_name: "bg-blue-100 text-blue-800 border-blue-200",
  bulk_action: "bg-gray-100 text-gray-700 border-gray-200",
};

function formatWhen(iso: string): string {
  const d = new Date(iso);
  const t = new Intl.DateTimeFormat(undefined, { hour: "2-digit", minute: "2-digit", hour12: false }).format(d);
  const day = new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric" }).format(d);
  return `${t} · ${day}`;
}

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

function daysAgoIso(n: number) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}

const PAGE_SIZE = 50;

export default function Activity() {
  const { context } = useSDK();
  const toast = useToast();

  // PR #66: defense-in-depth. A brand session must never see this canonical
  // admin view — App.tsx's synchronous route guard should redirect first, but if
  // it doesn't (stale bundle, direct mount) bounce to the brand-scoped activity
  // page before any admin data loads.
  useEffect(() => {
    let alive = true;
    fetch("/api/auth/me", { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => {
        if (!alive || !j) return;
        if (j.user?.role !== "admin" && j.tenant?.slug) {
          window.location.replace(`#/brand/${j.tenant.slug}/activity`);
        }
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  const [from, setFrom] = useState<string>(daysAgoIso(7));
  const [to, setTo] = useState<string>(todayIso());
  const [actions, setActions] = useState<string[]>([]);
  const [actor, setActor] = useState<string>("");
  const [q, setQ] = useState<string>("");
  const [qInput, setQInput] = useState<string>("");

  const [rows, setRows] = useState<AuditRow[]>([]);
  const [total, setTotal] = useState(0);
  const [cursors, setCursors] = useState<(string | null)[]>([null]);
  const [pageIndex, setPageIndex] = useState(0);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [actors, setActors] = useState<ActorRow[]>([]);

  const qs = useMemo(() => {
    const params = new URLSearchParams();
    params.set("limit", String(PAGE_SIZE));
    if (from) params.set("from", from);
    if (to) params.set("to", to);
    if (actions.length) params.set("actions", actions.join(","));
    if (actor) params.set("actor", actor);
    if (q) params.set("q", q);
    return params;
  }, [from, to, actions, actor, q]);

  const load = useCallback(
    async (page: number, cursorList: (string | null)[]) => {
      setLoading(true);
      try {
        const p = new URLSearchParams(qs);
        const cur = cursorList[page] ?? null;
        if (cur) p.set("cursor", cur);
        const r = await api.get(context, `/api/audit?${p.toString()}`);
        setRows(r.rows || []);
        setTotal(r.total_in_range || 0);
        setNextCursor(r.next_cursor || null);
      } catch (err) {
        toast.push(`Failed to load activity: ${(err as Error).message}`, "error");
      } finally {
        setLoading(false);
      }
    },
    [context, qs, toast]
  );

  useEffect(() => {
    setPageIndex(0);
    setCursors([null]);
    load(0, [null]);
  }, [qs, load]);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(context, "/api/audit/actors");
        setActors(r.rows || []);
      } catch {}
    })();
  }, [context]);

  const toggleAction = (v: string) => {
    setActions((prev) => (prev.includes(v) ? prev.filter((a) => a !== v) : [...prev, v]));
  };

  const toggleExpanded = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setQ(qInput.trim());
  };

  const exportCsv = async () => {
    try {
      const p = new URLSearchParams(qs);
      p.delete("limit");
      const res = await fetch(`/api/audit/export.csv?${p.toString()}`, {
        headers: {
          "x-heady-user-id": context?.userId || "standalone-user",
          "x-heady-team-id": context?.teamId || "standalone-team",
        },
      });
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "storesync-activity.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.push(`Export failed: ${(err as Error).message}`, "error");
    }
  };

  const goNext = () => {
    if (!nextCursor) return;
    const newCursors = [...cursors.slice(0, pageIndex + 1), nextCursor];
    setCursors(newCursors);
    setPageIndex(pageIndex + 1);
    load(pageIndex + 1, newCursors);
  };
  const goPrev = () => {
    if (pageIndex === 0) return;
    const next = pageIndex - 1;
    setPageIndex(next);
    load(next, cursors);
  };

  const pageStart = total === 0 ? 0 : pageIndex * PAGE_SIZE + 1;
  const pageEnd = Math.min(total, pageIndex * PAGE_SIZE + rows.length);

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-2xl font-semibold text-gray-900">Activity log</h2>
          <p className="text-sm text-gray-600 mt-1">Every match, change, and enrichment, recorded for audit.</p>
        </div>
        <Button variant="ghost" onClick={exportCsv}>Export CSV</Button>
      </div>

      <div className="sticky top-[52px] z-10 rounded-lg border border-gray-200 bg-white/95 backdrop-blur p-4 shadow-sm space-y-3">
        <div className="flex flex-wrap items-center gap-3">
          <label className="text-xs text-gray-500 flex items-center gap-1.5">
            From
            <input
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="text-sm border border-gray-200 rounded-md px-2 py-1"
            />
          </label>
          <label className="text-xs text-gray-500 flex items-center gap-1.5">
            To
            <input
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="text-sm border border-gray-200 rounded-md px-2 py-1"
            />
          </label>
          <select
            value={actor}
            onChange={(e) => setActor(e.target.value)}
            className="text-sm border border-gray-200 rounded-md px-2 py-1"
          >
            <option value="">All actors</option>
            {actors.map((a) => (
              <option key={a.actor_heady_user_id} value={a.actor_heady_user_id}>
                User {a.actor_heady_user_id.slice(0, 8)} ({a.event_count})
              </option>
            ))}
          </select>
          <form onSubmit={submitSearch} className="flex items-center gap-2">
            <input
              type="text"
              placeholder="Search license, row, payload…"
              value={qInput}
              onChange={(e) => setQInput(e.target.value)}
              className="text-sm border border-gray-200 rounded-md px-2 py-1 w-60"
            />
            <Button type="submit" variant="secondary">Search</Button>
            {q && (
              <button
                type="button"
                className="text-xs text-gray-500 hover:text-gray-800"
                onClick={() => { setQ(""); setQInput(""); }}
              >
                Clear
              </button>
            )}
          </form>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {ACTION_OPTIONS.map((o) => {
            const selected = actions.includes(o.value);
            return (
              <button
                key={o.value}
                type="button"
                onClick={() => toggleAction(o.value)}
                className={`text-xs rounded-full border px-2.5 py-1 transition-colors ${
                  selected
                    ? "bg-accent-500 text-white border-accent-500"
                    : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"
                }`}
              >
                {o.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white shadow-sm overflow-hidden">
        {loading && rows.length === 0 ? (
          <div className="p-10 text-center text-sm text-gray-500">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center">
            <div className="mx-auto h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-400 mb-2">○</div>
            <div className="text-sm font-medium text-gray-800">No activity yet.</div>
            <div className="text-xs text-gray-500 mt-1">Actions you take on the Review tab will appear here.</div>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="text-left px-4 py-2 font-medium">When</th>
                <th className="text-left px-4 py-2 font-medium">Actor</th>
                <th className="text-left px-4 py-2 font-medium">Action</th>
                <th className="text-left px-4 py-2 font-medium">License</th>
                <th className="text-left px-4 py-2 font-medium">Source row</th>
                <th className="text-left px-4 py-2 font-medium">Change</th>
                <th className="text-left px-4 py-2 font-medium w-8"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const isOpen = expanded.has(r.id);
                return (
                  <Fragment key={r.id}>
                    <tr className="border-t border-gray-100 hover:bg-gray-50 align-top">
                      <td className="px-4 py-2 whitespace-nowrap" title={r.occurred_at}>
                        {formatWhen(r.occurred_at)}
                      </td>
                      <td className="px-4 py-2">
                        <div className="text-gray-900">
                          {r.actor_heady_user_id ? `User ${r.actor_heady_user_id.slice(0, 8)}` : "—"}
                        </div>
                        {r.ip_address && (
                          <div className="text-xs text-gray-400">{r.ip_address}</div>
                        )}
                      </td>
                      <td className="px-4 py-2">
                        <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs ${ACTION_COLORS[r.action] || "bg-gray-100 text-gray-700 border-gray-200"}`}>
                          {r.action}
                        </span>
                      </td>
                      <td className="px-4 py-2 font-mono text-xs">
                        {r.license_number ? (
                          <button
                            className="hover:underline"
                            onClick={() => { setQInput(r.license_number!); setQ(r.license_number!); }}
                          >
                            {r.license_number}
                          </button>
                        ) : "—"}
                      </td>
                      <td className="px-4 py-2 font-mono text-xs">
                        {r.source_row_id ? (
                          <button
                            className="hover:underline"
                            onClick={() => { setQInput(r.source_row_id!); setQ(r.source_row_id!); }}
                          >
                            {r.source_row_id.length > 24 ? `${r.source_row_id.slice(0, 24)}…` : r.source_row_id}
                          </button>
                        ) : "—"}
                      </td>
                      <td className="px-4 py-2 text-gray-700">
                        {r.prev_status || r.new_status ? (
                          <span className="inline-flex items-center gap-1.5">
                            <span className="text-gray-500">{r.prev_status || "—"}</span>
                            <span className="text-gray-400">→</span>
                            <span className="text-gray-900">{r.new_status || "—"}</span>
                          </span>
                        ) : "—"}
                      </td>
                      <td className="px-4 py-2">
                        <button
                          className="text-gray-400 hover:text-gray-700"
                          onClick={() => toggleExpanded(r.id)}
                          aria-label={isOpen ? "Collapse" : "Expand"}
                        >
                          {isOpen ? "▾" : "▸"}
                        </button>
                      </td>
                    </tr>
                    {isOpen && (
                      <tr className="bg-gray-50 border-t border-gray-100">
                        <td colSpan={7} className="px-4 py-3">
                          <pre className="text-xs text-gray-700 whitespace-pre-wrap break-all">
{JSON.stringify(r.payload ?? {}, null, 2)}
                          </pre>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        )}
        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 text-xs text-gray-500">
          <div>
            {total > 0 ? `Showing ${pageStart}–${pageEnd} of ${total}` : "0 results"}
          </div>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={goPrev} disabled={pageIndex === 0 || loading}>Prev</Button>
            <Button variant="ghost" onClick={goNext} disabled={!nextCursor || loading}>Next</Button>
          </div>
        </div>
      </div>
    </div>
  );
}
